import { ApplicationConfig, provideBrowserGlobalErrorListeners, isDevMode, inject, provideAppInitializer, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { authInterceptor, provideAuth } from 'angular-auth-oidc-client';
import { provideServiceWorker } from '@angular/service-worker';
import { authConfig } from './core/config/auth.config';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { it } from 'date-fns/locale/it';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { httpInterceptor } from './core/interceptors/http-interceptor';


function matPaginatorIntlFactory(): MatPaginatorIntl {
  const intl = new MatPaginatorIntl();
  const transloco = inject(TranslocoService);
  const destroyRef = inject(DestroyRef);

  const applyLabels = () => {
    intl.itemsPerPageLabel = transloco.translate('itemsPerPage');
    intl.changes.next();
  };

  applyLabels();

  transloco.langChanges$.pipe(
    takeUntilDestroyed(destroyRef)
  ).subscribe(() => {
    applyLabels();
  });
  return intl;
}

const provideTheme = provideAppInitializer(() => {
  const theme = (localStorage.getItem('theme') as 'light' | 'dark') ?? 'light';
  document.documentElement.classList.add(theme === 'dark' ? 'dark-theme' : 'light-theme');
});

const provideLanguage = provideAppInitializer(() => {
  const translocoService = inject(TranslocoService);
  const lang = (localStorage.getItem('lang') as 'it' | 'en') ?? 'it';
  const langToLoad = ['it', 'en'].includes(lang) ? lang : 'it';
  translocoService.setActiveLang(langToLoad);
  return firstValueFrom(translocoService.load(langToLoad));
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideAuth(authConfig),
    provideHttpClient(withInterceptors([authInterceptor(), httpInterceptor])),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideTransloco({
      config: {
        availableLangs: ['en', 'it'],
        defaultLang: 'it',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader
    }),
    provideLanguage,
    provideTheme,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideDateFnsAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: it },
    {
      provide: MatPaginatorIntl,
      useFactory: matPaginatorIntlFactory
    }
  ]
};


